import { z } from 'zod';
import { EnrollmentStatusSchema, TrialEnrollmentDtoSchema } from './enrollments.schema.js';
// ---------------------------------------------------------------------------
// Qualification criteria of a trial (kryteria włączenia/wyłączenia, issue #30,
// REQ-PAT-018…020, REQ-PAT-023; frozen feature
// spec/features/patients-pathways/criteria-qualification.feature)
// ---------------------------------------------------------------------------
/**
 * R3: the fixed two-category vocabulary of a criterion (0020 CHECK
 * `trial_criteria_category_valid`). The UI groups the checklist into exactly
 * these two sections (Włączenia / Wyłączenia).
 */
export const CRITERION_CATEGORIES = {
    INCLUSION: 'inclusion',
    EXCLUSION: 'exclusion',
};
export const CriterionCategorySchema = z.enum(Object.values(CRITERION_CATEGORIES));
/** One configured criterion (configuration tab row; `trial_criteria` of 0020). */
export const TrialCriterionDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    /** REQ-PAT-018: the criterion has content; never empty. */
    content: z.string().min(1),
    category: CriterionCategorySchema,
    /** REQ-PAT-019: default both `true`; gate 5 forbids both `false`. */
    appliesMale: z.boolean(),
    appliesFemale: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * One row of the defined-answer list of a criterion (REQ-PAT-020;
 * `trial_criteria_answers` of 0020 — `answer` mirrors the varchar(255) column).
 */
export const TrialCriterionAnswerDtoSchema = z.object({
    id: z.number().int().positive(),
    tenantId: z.number().int().positive(),
    criteriaId: z.number().int().positive(),
    answer: z.string().min(1).max(255),
    met: z.boolean(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** A criterion with its defined answers — one row of the configuration list. */
export const CriterionConfigDtoSchema = z.object({
    criterion: TrialCriterionDtoSchema,
    answers: z.array(TrialCriterionAnswerDtoSchema),
});
/**
 * The whole configuration of one trial (`GET /trials/:trialId/criteria`).
 * `items` rather than a bare array (precedent: `PatientEnrollmentsDto`); no
 * pagination — the protocol criterion list is short and rendered whole.
 */
export const TrialCriteriaConfigDtoSchema = z.object({
    items: z.array(CriterionConfigDtoSchema),
});
/**
 * Gate 5 shared by both write requests: unchecking BOTH genders is forbidden —
 * a criterion must apply to at least one (0020 CHECK
 * `trial_criteria_at_least_one_gender` is the DB backstop). Absent flags
 * default to `true`, exactly like the service defaults them.
 */
function atLeastOneGender(value) {
    return (value.appliesMale ?? true) || (value.appliesFemale ?? true);
}
const GENDER_MESSAGE = 'Kryterium musi dotyczyć co najmniej jednej płci';
/** `POST /trials/:trialId/criteria` — defaults: category `inclusion`, both genders. */
export const CreateCriterionRequestSchema = z
    .object({
    content: z.string().trim().min(1),
    category: CriterionCategorySchema.optional(),
    appliesMale: z.boolean().optional(),
    appliesFemale: z.boolean().optional(),
})
    .refine(atLeastOneGender, { message: GENDER_MESSAGE });
/**
 * `PATCH /trials/:trialId/criteria/:criteriaId` — partial by design; the
 * service merges the patch with the stored row before the gate-5 check, so a
 * patch carrying ONE unchecked box is caught there. The refine below rejects
 * the case the dialog itself can produce in one payload: both boxes unchecked.
 */
export const UpdateCriterionRequestSchema = z
    .object({
    content: z.string().trim().min(1).optional(),
    category: CriterionCategorySchema.optional(),
    appliesMale: z.boolean().optional(),
    appliesFemale: z.boolean().optional(),
})
    .refine(atLeastOneGender, { message: GENDER_MESSAGE });
/** `POST /trials/:trialId/criteria/:criteriaId/answers` (REQ-PAT-020). */
export const AddCriterionAnswerRequestSchema = z.object({
    answer: z.string().trim().min(1).max(255),
    met: z.boolean(),
});
// ---------------------------------------------------------------------------
// Patient qualification checklist and decision (dialog in the chart,
// `patients:write`; gate 4 incremental save, gate R4 atomic decision)
// ---------------------------------------------------------------------------
/** One defined answer as presented in the dialog — minimal (text + category). */
export const DefinedAnswerDtoSchema = z.object({
    id: z.number().int().positive(),
    answer: z.string().min(1).max(255),
    met: z.boolean(),
});
/**
 * The saved checklist cell of one criterion (REQ-PAT-023): a text COPY of the
 * chosen defined answer or free text, plus the met mark and the optional
 * comment. Deliberately no FK-derived fields — an amendment of the defined
 * list never changes what was recorded.
 */
export const SavedChecklistAnswerDtoSchema = z.object({
    answer: z.string().min(1),
    met: z.boolean(),
    comments: z.string().nullable(),
    updatedAt: z.iso.datetime(),
});
/** One criterion row of the qualification dialog (CRITERIA | VALUE | COMMENT). */
export const QualificationCriterionDtoSchema = z.object({
    id: z.number().int().positive(),
    content: z.string().min(1),
    category: CriterionCategorySchema,
    appliesMale: z.boolean(),
    appliesFemale: z.boolean(),
    definedAnswers: z.array(DefinedAnswerDtoSchema),
    savedAnswer: SavedChecklistAnswerDtoSchema.nullable(),
});
/**
 * The derived, INFORMATIONAL satisfaction summary (R3 + gate 3): it never
 * blocks the decision. `exclusionActive` counts exclusion criteria answered
 * `met: false` — for an exclusion criterion "not met" means the exclusion
 * condition IS present.
 */
export const QualificationSummaryDtoSchema = z.object({
    criteriaTotal: z.number().int().nonnegative(),
    inclusionTotal: z.number().int().nonnegative(),
    inclusionMet: z.number().int().nonnegative(),
    exclusionTotal: z.number().int().nonnegative(),
    exclusionActive: z.number().int().nonnegative(),
});
/**
 * Everything the qualification dialog renders (`GET
 * /patients/:patientId/enrollments/:enrollmentId/qualification`): the trial's
 * criteria already filtered by the person's sex (REQ-PAT-019), the saved
 * cells, the summary and the decision availability (gate 1: only while the
 * status is `pre_screening`).
 */
export const QualificationChecklistDtoSchema = z.object({
    enrollmentId: z.number().int().positive(),
    trialId: z.number().int().positive(),
    status: EnrollmentStatusSchema,
    decisionAvailable: z.boolean(),
    criteria: z.array(QualificationCriterionDtoSchema),
    summary: QualificationSummaryDtoSchema,
});
/**
 * One checklist cell to save (`PUT …/qualification/answers`, gate 4: results
 * arrive over days and each persists immediately). Re-saving the same
 * (enrollment, criterion) updates the cell in place. Blank comments normalize
 * to `null` in the service.
 */
export const SaveChecklistAnswerRequestSchema = z.object({
    criteriaId: z.number().int().positive(),
    answer: z.string().trim().min(1),
    met: z.boolean(),
    comments: z.string().trim().nullish(),
});
export const SaveChecklistAnswerResponseSchema = z.object({
    answer: SavedChecklistAnswerDtoSchema,
});
/**
 * The two R4 decision verbs: positive `pre_screening → screened`, negative
 * `pre_screening → screening_failure`. `screened → ongoing` (enrollment /
 * randomization) is the #29 cockpit boundary and NOT part of this contract.
 */
export const QUALIFICATION_DECISIONS = ['positive', 'negative'];
export const QualificationDecisionSchema = z.enum(QUALIFICATION_DECISIONS);
/**
 * `POST …/qualification/decision` (gate 2): the decision carries the checklist
 * state submitted with it and the backend persists both atomically. `answers`
 * may be empty — a decision on an empty/incomplete checklist is allowed
 * (gate 3) and incrementally saved cells are already durable.
 */
export const DecideQualificationRequestSchema = z.object({
    decision: QualificationDecisionSchema,
    answers: z.array(SaveChecklistAnswerRequestSchema).optional(),
});
/** The participation after the atomic transition — the chart re-renders it. */
export const DecideQualificationResponseSchema = z.object({
    enrollment: TrialEnrollmentDtoSchema,
});
//# sourceMappingURL=criteria.schema.js.map